import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import LocalizedStrings from 'react-localization';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { CircularProgress, DialogActions } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import { DialogContent } from "@material-ui/core";
import Typography from "@mui/material/Typography";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import { PRIMARY_THEME_COLOR_SHADE_1 } from "../../../lib/constants";
import AlertDialog from "../../../components/AlertDialog/index.es6";
import MultipleSelectChip from "../../PartnerStatus/components/Edit/MultipleSelectChip";
import { localisedText } from "../../../lib/localisation.es6";
import { getPartnerGuid } from "../../../lib/access.es6";
import { buildUserUpdatePayload, partnersForSave } from "./userUpdatePayload";

const RESERVED_ATTRS = ['partners', 'isAgent'];

function parseAttrs(userAttrs) {
    return Object.keys(userAttrs)
        .filter(function(key) { return RESERVED_ATTRS.indexOf(key) === -1; })
        .map(function(key) {
            return { key: key, value: userAttrs[key][0] || '', isNew: false };
        });
}

function formatAttrKey(key) {
    return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^[a-z]/, function(c) { return c.toUpperCase(); });
}

const EditUser = (props) => {
    if (!props.user) {
        return ""
    }

    const userAttrs = props.user.attributes || {};

    const [labels] = useState(new LocalizedStrings(localisedText));
    const [firsName, setFirstName] = useState(props.user.firstName);
    const [lastName, setLastName] = useState(props.user.lastName);
    const [currentPartners, setCurrentPartners] = useState(props.currentUserPartners);
    const [filteredPartners, setFilteredPartners] = useState([]);
    const [isAgent, setIsAgent] = useState(userAttrs.isAgent ? userAttrs.isAgent[0] === 'true' : false);
    const [dynamicAttrs, setDynamicAttrs] = useState(parseAttrs(userAttrs));

    useEffect(() => {
        setCurrentPartners(props.currentUserPartners);
    }, [props.currentUserPartners]);

    useEffect(() => {
        if (!props.allPartners) return;

        if (getPartnerGuid() === 'omk') {
            const p = props.allPartners.find(function(p) { return p === 'omk'; });
            setFilteredPartners(p ? [p] : []);
        } else if (getPartnerGuid() === 'cover_link') {
            const p = props.allPartners.find(function(p) { return p === 'cover_link'; });
            setFilteredPartners(p ? [p] : []);
        } else if (getPartnerGuid() === 'old_mutual_malawi') {
            const p = props.allPartners.find(function(p) { return p === 'old_mutual_malawi'; });
            setFilteredPartners(p ? [p] : []);
        } else if (props.allPartners.length > 0) {
            setFilteredPartners(props.allPartners);
        }
    }, [props.allPartners]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name') setFirstName(value);
        if (name === 'lastname') setLastName(value);
    };

    const handleAttrValueChange = (index, value) => {
        setDynamicAttrs(function(prev) {
            return prev.map(function(a, i) {
                return i === index ? Object.assign({}, a, { value: value }) : a;
            });
        });
    };

    const handleAttrKeyChange = (index, key) => {
        setDynamicAttrs(function(prev) {
            return prev.map(function(a, i) {
                return i === index ? Object.assign({}, a, { key: key }) : a;
            });
        });
    };

    const addAttr = () => {
        setDynamicAttrs(function(prev) {
            return prev.concat([{ key: '', value: '', isNew: true }]);
        });
    };

    const removeAttr = (index) => {
        setDynamicAttrs(function(prev) {
            return prev.filter(function(_, i) { return i !== index; });
        });
    };

    const handleSubmit = () => {
        var attrsPayload = {};
        dynamicAttrs.forEach(function(a) {
            if (a.key) {
                attrsPayload[a.key] = [a.value];
            }
        });
        attrsPayload.isAgent = [String(isAgent)];
        attrsPayload.partners = partnersForSave(
            currentPartners,
            isAgent ? getPartnerGuid() : null
        );

        props.editUser(buildUserUpdatePayload(props.user, {
            firstName: firsName,
            lastName: lastName,
            attributes: attrsPayload,
        }));
    };

    const reset = () => {
        setFirstName('');
        setLastName('');
        setCurrentPartners([]);
        setIsAgent(false);
        setDynamicAttrs([]);
    };

    const handleUserEditedSuccessfully = () => {
        props.resetEditUserSuccessAlert();
        props.resetUserToEdit();
        reset();
    };

    const handleUserCreateError = () => {
        props.resetEditUserErrorAlert();
    };

    const cancelEdit = () => {
        props.resetUserToEdit();
    };

    return (
        <>
            <AlertDialog
                custom
                show={props.showEditUserProgressAlert}
                size="sm"
                style={{ marginTop: '0', top: '30vh' }}
                confirmBtnText={'ok'}
                showCancel={false}
                showConfirm={false}
                cancelBtnText={'cancel'}
                showTitle={false}
                confirmBtnCssClass
            >
                <CircularProgress />
                <h2>Updating User</h2>
            </AlertDialog>
            <AlertDialog
                success
                show={props.showEditUserSuccessAlert}
                size="sm"
                title={'User updated'}
                style={{ marginTop: '0', top: '30vh' }}
                onConfirm={() => { handleUserEditedSuccessfully(); }}
                confirmBtnText={'ok'}
                showCancel={false}
                showConfirm={false}
                cancelBtnText={'cancel'}
                confirmBtnCssClass
            >
                <div>
                    <Button variant="contained" onClick={() => { handleUserEditedSuccessfully(); }} style={{ background: 'green' }}>Ok</Button>
                </div>
            </AlertDialog>
            <AlertDialog
                show={props.showEditUserErrorAlert}
                danger
                title={'Error updating User'}
                onConfirm={() => handleUserCreateError()}
                confirmBtnText={'Try again'}
                confirmBtnCssClass
                showCancel={false}
                style={{ marginTop: '0', top: '30vh' }}
            >
                <p>
                    {props.editUserError && props.editUserError.errorMessage
                        ? props.editUserError.errorMessage
                        : "Error updating User"}
                </p>
            </AlertDialog>
            <Dialog
                open={props.user}
                fullWidth
                maxWidth='lg'
                onClose={props.resetUserToEdit}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={props.resetUserToEdit}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Edit User
                        </Typography>
                    </Toolbar>
                </AppBar>
                <ValidatorForm onSubmit={() => handleSubmit()}>
                    <DialogContent>
                        <Grid container spacing={4} style={{ marginTop: '2%' }}>

                            <Grid item md={6} sm={6} xs={12}>
                                <TextValidator
                                    onChange={handleChange}
                                    label={'First name'}
                                    value={firsName}
                                    name='name'
                                    fullWidth
                                    variant="outlined"
                                    type="text"
                                    validators={['required']}
                                    errorMessages={['Please enter first name']}
                                />
                            </Grid>
                            <Grid item md={6} sm={6} xs={12}>
                                <TextValidator
                                    onChange={handleChange}
                                    label={'Last Name'}
                                    value={lastName}
                                    name='lastname'
                                    fullWidth
                                    variant="outlined"
                                    type="text"
                                    validators={['required']}
                                    errorMessages={['Please enter last name']}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup aria-label="position" row>
                                    <FormControlLabel
                                        checked={isAgent}
                                        onChange={function(e) { setIsAgent(e.target.checked); }}
                                        control={<Checkbox size="small" />}
                                        label="Is Agent?"
                                        labelPlacement="start"
                                    />
                                </FormGroup>
                            </Grid>

                            <Grid item md={6} sm={6} xs={12}>
                                <MultipleSelectChip
                                    constantFields={filteredPartners}
                                    fieldLabel="partners"
                                    fields={currentPartners}
                                    setFields={setCurrentPartners}
                                    allPartners={filteredPartners}
                                />
                            </Grid>

                            {dynamicAttrs.map(function(attr, index) {
                                return (
                                    <Grid item xs={12} key={index}>
                                        <Grid container spacing={2} alignItems="center">
                                            {attr.isNew && (
                                                <Grid item md={5} sm={5} xs={12}>
                                                    <TextField
                                                        label="Attribute Name"
                                                        value={attr.key}
                                                        fullWidth
                                                        variant="outlined"
                                                        placeholder="e.g. staffId"
                                                        onChange={function(e) { handleAttrKeyChange(index, e.target.value); }}
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item md={attr.isNew ? 6 : 11} sm={attr.isNew ? 6 : 11} xs={10}>
                                                <TextField
                                                    label={attr.isNew ? 'Value' : formatAttrKey(attr.key)}
                                                    value={attr.value}
                                                    fullWidth
                                                    variant="outlined"
                                                    onChange={function(e) { handleAttrValueChange(index, e.target.value); }}
                                                />
                                            </Grid>
                                            <Grid item md={1} sm={1} xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                                                <IconButton
                                                    onClick={function() { removeAttr(index); }}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                );
                            })}

                            <Grid item xs={12}>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addAttr}
                                    variant="outlined"
                                    size="small"
                                    style={{ borderColor: PRIMARY_THEME_COLOR_SHADE_1, color: PRIMARY_THEME_COLOR_SHADE_1 }}
                                >
                                    Add Attribute
                                </Button>
                            </Grid>

                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button variant="contained" type="submit" style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
                            {labels.updateUser}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={cancelEdit}
                            style={{ marginRight: '3%', borderColor: PRIMARY_THEME_COLOR_SHADE_1, color: PRIMARY_THEME_COLOR_SHADE_1, fontWeight: 'bold' }}
                        >
                            {labels.cancel}
                        </Button>
                    </DialogActions>
                </ValidatorForm>
            </Dialog>
        </>
    );
};

export default EditUser;
